import type { VipPackage, PagePackage, PaymentStatus } from '~/stores/pay'

// 支付相关API封装（Phase3 第11步：接入套餐组 + 下单/状态占位）
export const usePayApi = () => {
  const api = useNuxtApp().$api as <T = any>(
    path: string,
    opts?: { method?: string; body?: any }
  ) => Promise<any>
  const config = useRuntimeConfig()
  const payAppId = config.public.payAppId

  // 通用：拉取套餐组（真实后端）
  async function listPayPackageGroup(scenes: string[]) {
    const resp = await api('/paypackagegroup/list_group', { method: 'POST', body: { scene_list: scenes } })
    const d: any = (resp as any)?.data ?? resp
    return (d?.pay_groups || []) as any[]
  }

  // ================= 套餐列表 =================
  async function fetchVipPackages(): Promise<{ packages: VipPackage[]; defaultProductId?: string }>{
    const groups = await listPayPackageGroup(['vip_page'])
    const group = groups.find((g: any) => g.scene === 'vip_page')
    const pkgs: VipPackage[] = (group?.pay_packages || []).map((p: any) => mapVipPackage(p))
    return { packages: pkgs, defaultProductId: group?.default_product_id }
  }

  async function fetchPagePackages(): Promise<{ packages: PagePackage[]; defaultProductId?: string }>{
    const groups = await listPayPackageGroup(['translate_cnt_page'])
    const group = groups.find((g: any) => g.scene === 'translate_cnt_page')
    const pkgs: PagePackage[] = (group?.pay_packages || []).map((p: any) => mapPagePackage(p))
    return { packages: pkgs, defaultProductId: group?.default_product_id }
  }

  // ================ 下单/状态 ================
  async function createPaymentOrder(params: { productId: string; paymentMethod: string }) {
    if (params.paymentMethod !== 'wechat') {
      throw new Error('Only WeChat payment is supported')
    }
    
    const resp = await api('/payv2/wx/native', {
      method: 'POST',
      body: {
        payapp: payAppId,
        product_id: params.productId,
      }
    })
    const d = (resp as any)?.data ?? resp
    // 响应结构：{ order_id, wx_resp: { code_url } }
    return { 
      orderId: d.order_id, 
      codeUrl: d.wx_resp?.code_url 
    }
  }

  async function checkPaymentStatus(orderId: string): Promise<{ status: PaymentStatus, order: any }> {
    const resp = await api('/payv2/order/query', {
      method: 'POST',
      body: {
        payapp: payAppId,
        order_id: orderId
      }
    })
    const d = (resp as any)?.data ?? resp
    
    // 直接使用后端返回的 status 字符串，保持定义一致
    // 后端返回: "paid", "paying", "fail", "refund", "" 等
    const serverStatus = d.order?.status as PaymentStatus
    
    return { status: serverStatus, order: d.order }
  }

  // ================= 映射辅助 =================
  // 价格单位转换：后端 fee 为“分”，前端显示为“元”
  function toYuan(fee: number) { return Number(((fee || 0) / 100).toFixed(2)) }

  function mapVipPackage(p: any): VipPackage {
    const fee = toYuan(p.fee)
    const dd = p.display_detail || {}
    const upText = dd?.up_section?.up_section_text || ''
    const lowerText = dd?.lower_section?.lower_section_text || ''
    const titleTagText = dd?.title_tag?.title_tag_text || ''

    // 尝试从下部文案里解析原价（如“原价¥199”）
    let originalPrice: number | undefined
    const m = (lowerText || '').match(/原价¥\s*([0-9]+(?:\.[0-9]+)?)/)
    if (m) originalPrice = Number(m[1])

    return {
      productId: p.product_id,
      name: upText || p.product_id,
      price: fee,
      subtitle: lowerText,     // 新增：副标题，直接渲染下部提示
      originalPrice,
      tagText: titleTagText || undefined,
      footerText: dd?.footer?.footer_text || undefined,
      footerStyle: dd?.footer?.footer_style,
    }
  }

  function mapPagePackage(p: any): PagePackage {
    const fee = toYuan(p.fee)
    const dd = p.display_detail || {}
    const upText = dd?.up_section?.up_section_text || p.product_id
    const lowerText = dd?.lower_section?.lower_section_text || ''
    const titleDigits = upText.match(/(\d+)/) || p.product_id.match(/(\d+)/)
    const pages = Number((titleDigits || [])[1] || 0)
    const unitFromText = lowerText.match(/¥\s*([0-9.]+)\/页/)
    const unit = unitFromText ? Number(unitFromText[1]) : (pages > 0 ? Number((fee / pages).toFixed(2)) : 0)
    // 解析原价（如“原价¥45”）
    let originalPrice: number | undefined
    const m = (lowerText || '').match(/原价¥\s*([0-9]+(?:\.[0-9]+)?)/)
    if (m) originalPrice = Number(m[1])
    return {
      productId: p.product_id,
      name: upText,
      pages,
      price: fee,
      originalPrice,
      unitPrice: unit,
      tagText: dd?.title_tag?.title_tag_text || undefined,
      subtitle: lowerText || undefined,
      footerText: dd?.footer?.footer_text || undefined,
      footerStyle: dd?.footer?.footer_style
    }
  }

  return { fetchVipPackages, fetchPagePackages, createPaymentOrder, checkPaymentStatus }
}
